// Date helpers for the "date" dynamic-field type in AI templates.
// The value substituted into the prompt is a human-readable date string in the
// language the admin picked for the field (Bangla or English).

export const todayISO = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// "2026-08-18" → "১৮ আগস্ট, ২০২৬" (bn), "18 August 2026" (en) or "١٨ أغسطس ٢٠٢٦" (ar).
// ar-EG keeps the Gregorian calendar with Arabic month names and digits (ar-SA
// would switch to the Hijri calendar). Falls back to the raw string if it isn't
// a parseable ISO date.
const DATE_LOCALES = { bn: 'bn-BD', en: 'en-GB', ar: 'ar-EG' };
export const formatPromptDate = (iso, lang = 'bn') => {
    const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
    if (Number.isNaN(d.getTime())) return iso || '';
    return d.toLocaleDateString(DATE_LOCALES[lang] || DATE_LOCALES.bn, {
        day: 'numeric', month: 'long', year: 'numeric',
    });
};

const toBnDigits = (n) => String(n).replace(/\d/g, (x) => '০১২৩৪৫৬৭৮৯'[x]);

// বঙ্গাব্দ (Bangladesh revised calendar, 2019): each Bangla month starts on a
// fixed Gregorian date — বৈশাখ on 14 April, and so on. Intl has no support for
// it, so the month is found by picking the latest start date on/before `d`.
const BANGLA_MONTH_STARTS = [
    { name: 'বৈশাখ', m: 3, d: 14 }, { name: 'জ্যৈষ্ঠ', m: 4, d: 15 },
    { name: 'আষাঢ়', m: 5, d: 15 }, { name: 'শ্রাবণ', m: 6, d: 16 },
    { name: 'ভাদ্র', m: 7, d: 16 }, { name: 'আশ্বিন', m: 8, d: 16 },
    { name: 'কার্তিক', m: 9, d: 17 }, { name: 'অগ্রহায়ণ', m: 10, d: 16 },
    { name: 'পৌষ', m: 11, d: 16 }, { name: 'মাঘ', m: 0, d: 15 },
    { name: 'ফাল্গুন', m: 1, d: 14 }, { name: 'চৈত্র', m: 2, d: 15 },
];
export const formatBanglaCalendarDate = (iso) => {
    const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
    if (Number.isNaN(d.getTime())) return iso || '';
    const ref = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let best = null;
    for (const y of [ref.getFullYear() - 1, ref.getFullYear()]) {
        for (const mo of BANGLA_MONTH_STARTS) {
            const start = new Date(y, mo.m, mo.d);
            if (start <= ref && (!best || start > best.start)) best = { start, name: mo.name };
        }
    }
    const day = Math.round((ref - best.start) / 86400000) + 1;
    const bnYear = ref >= new Date(ref.getFullYear(), 3, 14)
        ? ref.getFullYear() - 593
        : ref.getFullYear() - 594;
    return `${toBnDigits(day)} ${best.name}, ${toBnDigits(bnYear)}`;
};

// Hijri (Umm al-Qura) date, built from parts because the plain formatter
// appends the era ("যুগ" in Bangla, "هـ" in Arabic).
const formatHijriDate = (iso, locale, sep) => {
    const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
    if (Number.isNaN(d.getTime())) return iso || '';
    const parts = new Intl.DateTimeFormat(`${locale}-u-ca-islamic-umalqura`, {
        day: 'numeric', month: 'long', year: 'numeric',
    }).formatToParts(d);
    const get = (t) => parts.find(p => p.type === t)?.value || '';
    return `${get('day')} ${get('month')}${sep} ${get('year')}`;
};
// Bangla script: "১২ রবিউল আউয়াল, ১৪৪৮"
export const formatHijriBanglaDate = (iso) => formatHijriDate(iso, 'bn-BD', ',');
// Arabic script: "١٢ ربيع الأول ١٤٤٨"
export const formatHijriArabicDate = (iso) => formatHijriDate(iso, 'ar-SA', '');

// Auto-date prompt variables. Any of these placed in a template prompt as
// {key} is replaced with today's date in that format at generation time.
export const DATE_VARIANTS = [
    { key: 'date-bn', format: (iso) => formatBanglaCalendarDate(iso) },
    { key: 'date-en-bn', format: (iso) => formatPromptDate(iso, 'bn') },
    { key: 'date-ar-bn', format: (iso) => formatHijriBanglaDate(iso) },
    { key: 'date-ar', format: (iso) => formatHijriArabicDate(iso) },
    { key: 'date-en', format: (iso) => formatPromptDate(iso, 'en') },
];
export const dateVariantValues = (iso) =>
    Object.fromEntries(DATE_VARIANTS.map(v => [v.key, v.format(iso)]));
